// Хуки для Form1

import { useState, useRef, useEffect } from 'react';
import { API_BASE } from '../../../config';
import { getAuthHeaders } from '../../../utils/authUtils';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Point } from 'ol/geom';
import { Feature } from 'ol';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Icon } from 'ol/style';
import { DoubleClickZoom } from 'ol/interaction';

// Москва — пока в карточке координат нет.
export const DEFAULT_MAP_POSITION = { lat: 55.7558, lng: 37.6176 };
// Масштаб карты: обзорный — без координат, ближе — к точке карточки.
const OVERVIEW_ZOOM = 8;
const POINT_ZOOM = 14;

// Координаты карточки для карты. В открытой форме они лежат в lat/lon
// ({ lat, lng }), в сохранённой карточке — в latitude/longitude строками.
// Нет координат или они вне допустимых пределов — null.
export const cardMapPosition = (data) => {
  if (!data) return null;
  const lat = parseFloat(data.lat && data.lat.lat !== undefined ? data.lat.lat : data.latitude);
  const lng = parseFloat(data.lon && data.lon.lng !== undefined ? data.lon.lng : data.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
};

// Хук для работы с картой
export const useMapModal = (localFormData, setLocalFormData, setFormData) => {
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapPosition, setMapPosition] = useState(DEFAULT_MAP_POSITION);
  const mapZoomRef = useRef(OVERVIEW_ZOOM);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerSourceRef = useRef(null);
  const isUserClickRef = useRef(false); // Флаг для отслеживания клика пользователя

  // Карта открывается на точке карточки, а не на Москве: иначе при просмотре
  // сохранённой карточки указанное место приходилось искать заново.
  const openMap = () => {
    const saved = cardMapPosition(localFormData);
    if (saved) {
      setMapPosition(saved);
      mapZoomRef.current = POINT_ZOOM;
    } else {
      mapZoomRef.current = OVERVIEW_ZOOM;
    }
    setShowMapModal(true);
  };

  const closeMap = () => {
    setShowMapModal(false);
  };

  // Функция для получения текущего местоположения
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Геолокация не поддерживается вашим браузером');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Округляем координаты до 6 знаков после запятой
        const lat = parseFloat(position.coords.latitude.toFixed(6));
        const lng = parseFloat(position.coords.longitude.toFixed(6));
        setMapPosition({ lat, lng });
      },
      (error) => {
        switch(error.code) {
          case error.PERMISSION_DENIED:
            alert('Доступ к местоположению запрещён. Разрешите доступ в настройках браузера.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Информация о местоположении недоступна.');
            break;
          case error.TIMEOUT:
            alert('Время ожидания запроса местоположения истекло.');
            break;
          default:
            alert('Произошла неизвестная ошибка при получении местоположения.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  // Функция для выбора координат
  const selectCoordinates = async (lat, lng) => {
    // Округляем координаты до 6 знаков после запятой
    const roundedLat = parseFloat(lat.toFixed(6));
    const roundedLng = parseFloat(lng.toFixed(6));
    
    // Получить высоту над уровнем моря через API
    let vnum = '';
    try {
      const resp = await fetch(`${API_BASE}/elevation/?lat=${roundedLat}&lng=${roundedLng}`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      if (data && data.results && data.results[0] && data.results[0].elevation !== undefined) {
        vnum = String(Math.round(data.results[0].elevation));
      }
    } catch (e) {
      // Ошибка при получении высоты
    }

    const newData = { 
      ...localFormData, 
      lat: { lat: roundedLat, lng: roundedLng },
      lon: { lat: roundedLat, lng: roundedLng },
      vnum: vnum
    };
    setLocalFormData(newData);
    if (setLocalFormData) {
      setLocalFormData(prev => ({ 
        ...prev, 
        lat: { lat: roundedLat, lng: roundedLng },
        lon: { lat: roundedLat, lng: roundedLng },
        vnum: vnum
      }));
    }
    setShowMapModal(false);
  };

  // Инициализация карты OpenLayers
  useEffect(() => {
    if (showMapModal && mapRef.current && !mapInstance.current) {
      // Создаем векторный слой для маркера
      const markerSource = new VectorSource();
      markerSourceRef.current = markerSource;
      const markerLayer = new VectorLayer({
        source: markerSource,
        style: new Style({
          image: new Icon({
            anchor: [0.5, 1],
            src: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="8" fill="red"/></svg>'
          })
        })
      });

      // Создаем карту
      const map = new Map({
        target: mapRef.current,
        layers: [markerLayer],
        view: new View({
          center: fromLonLat([mapPosition.lng, mapPosition.lat]),
          zoom: mapZoomRef.current
        })
      });

      // Отключаем стандартное взаимодействие двойного клика (zoom out)
      const doubleClickZoom = map.getInteractions().getArray().find(
        interaction => interaction instanceof DoubleClickZoom
      );
      if (doubleClickZoom) {
        map.removeInteraction(doubleClickZoom);
      }

      mapInstance.current = map;

      // Подложка — векторные тайлы с нашего сервера, а не с tile.openstreetmap.org
      // (см. baseMapStyle.js). Её код со стилем весит заметно, а карту открывают
      // нечасто, поэтому он догружается только сейчас, отдельным файлом.
      import('./baseMap')
        .then(({ createBaseMapLayer }) => {
          if (mapInstance.current === map) {
            map.getLayers().insertAt(0, createBaseMapLayer());
          }
        })
        .catch((error) => console.error('Не удалось загрузить подложку карты', error));

      // Функция обновления маркера
      const updateMarker = (lat, lng) => {
        markerSource.clear();
        const feature = new Feature({
          geometry: new Point(fromLonLat([lng, lat]))
        });
        markerSource.addFeature(feature);
      };

      // Обновляем маркер при инициализации
      updateMarker(mapPosition.lat, mapPosition.lng);

      // Обработчик клика - обновляем только маркер, НЕ центр карты
      map.on('click', (event) => {
        isUserClickRef.current = true;
        const coords = toLonLat(event.coordinate);
        // Округляем координаты до 6 знаков после запятой
        const newLat = parseFloat(coords[1].toFixed(6));
        const newLng = parseFloat(coords[0].toFixed(6));
        
        // Обновляем маркер напрямую в месте клика
        updateMarker(newLat, newLng);
        
        // Обновляем state только для отображения координат
        setMapPosition({ lng: newLng, lat: newLat });
        
        // Сохраняем и восстанавливаем зум
        const view = map.getView();
        const savedZoom = view.getZoom();
        requestAnimationFrame(() => {
          if (view.getZoom() !== savedZoom) {
            view.setZoom(savedZoom);
          }
        });
        
        setTimeout(() => {
          isUserClickRef.current = false;
        }, 100);
      });

      return () => {
        if (mapInstance.current) {
          mapInstance.current.setTarget(null);
          mapInstance.current = null;
        }
        markerSourceRef.current = null;
      };
    }
  }, [showMapModal]);

  // Обновляем центр карты и маркер только при getCurrentLocation (НЕ при клике пользователя)
  useEffect(() => {
    if (mapInstance.current && markerSourceRef.current && !isUserClickRef.current) {
      // Это НЕ клик пользователя - обновляем центр (например, при getCurrentLocation)
      const view = mapInstance.current.getView();
      const currentZoom = view.getZoom();
      view.setCenter(fromLonLat([mapPosition.lng, mapPosition.lat]));
      if (currentZoom !== undefined) {
        view.setZoom(currentZoom);
      }
      
      // Обновляем маркер
      markerSourceRef.current.clear();
      const feature = new Feature({
        geometry: new Point(fromLonLat([mapPosition.lng, mapPosition.lat]))
      });
      markerSourceRef.current.addFeature(feature);
    }
    // При клике пользователя маркер уже обновлен в обработчике клика, центр не трогаем
  }, [mapPosition]);

  // Функция для обновления позиции карты (для ручного ввода координат)
  const updateMapPosition = (lat, lng) => {
    // Валидация координат
    const validLat = Math.max(-90, Math.min(90, lat));
    const validLng = Math.max(-180, Math.min(180, lng));
    setMapPosition({ lat: validLat, lng: validLng });
  };

  return {
    showMapModal,
    mapPosition,
    mapRef,
    openMap,
    closeMap,
    getCurrentLocation,
    selectCoordinates,
    updateMapPosition
  };
};

